import { Controller } from "@hotwired/stimulus"
import DataLoader from "#lib/data_loader"

// CP2020 computed (derived) stats — SAVE, BTM, BDM, Initiative, Run, Leap, Carry.
// All derive from the base stats (+ SA initiative bonus), nothing stored. Listens
// for cp2020:stats-changed (the stats grid broadcasts on edit) and for role/SA
// changes (Initiative folds in Combat-Sense-style SA bonuses), then recomputes
// and writes each value into its data-target.
//
// Formulas (CP2020 core):
//   SAVE       = BODY
//   BTM        = BODY -> Body Type Modifier table (defense; negative)
//   BDM        = BODY -> Damage Modifier table (melee damage added)
//   Initiative = current REF (base + bonus) + SA initiative bonus
//   Run        = MA x 3   (meters this combat round)
//   Leap       = Run / 4
//   Carry      = BODY x 10 (kg)
export default class extends Controller {
  static targets = ["save", "btm", "bdm", "initiative", "run", "leap", "carry", "awareness", "hl"]
  // Outlets: the sheet form (base stat reads), the skills editor (Awareness
  // effective value), and the cyberware editor (HL — total Humanity Cost).
  static outlets = ["cp2020--sheet", "cp2020--skills", "cp2020--cyberware"]

  // The cyberware editor connecting is a readiness signal — recompute so its HL
  // total lands on load (mirrors how a change broadcast triggers recompute).
  cp2020CyberwareOutletConnected() {
    this.recompute()
  }

  // The sheet form connecting means base stats are now readable — recompute so the
  // derived stats land on load regardless of controller connection order.
  cp2020SheetOutletConnected() {
    this.recompute()
  }

  // BODY -> category breakpoints (Body Types & Points).
  static categoryFor(body) {
    if (body <= 2) return "very_weak"
    if (body <= 4) return "weak"
    if (body <= 7) return "average"
    if (body <= 9) return "strong"
    if (body <= 10) return "very_strong"
    if (body <= 12) return "bt_11_12"
    if (body <= 14) return "bt_13_14"
    return "bt_15_plus"
  }

  // Body Type Modifier (damage RESISTED) — negative; "Superhuman -5" caps it.
  static BTM = {
    very_weak: 0, weak: -1, average: -2, strong: -3, very_strong: -4,
    bt_11_12: -5, bt_13_14: -5, bt_15_plus: -5,
  }

  // Damage Modifier (melee damage ADDED).
  static BDM = {
    very_weak: -2, weak: -1, average: 0, strong: 1, very_strong: 2,
    bt_11_12: 4, bt_13_14: 6, bt_15_plus: 8,
  }

  connect() {
    DataLoader.systemPreload("sa-skill-bonuses")
    this.recompute()
  }

  async recompute() {
    // Always-on cyberware stat bonuses (edit sheet), folded into the effective
    // stats so every derivation below reflects the chrome. Activatable speedware is
    // excluded (show-mode toggle). Pulled from the cyberware editor via outlet.
    const cyber = await this.cyberStatBonuses()
    const body = this.stat("body") + (cyber.BODY || 0)
    const ref = this.stat("ref") + (cyber.REF || 0)
    const ma = this.stat("ma") + (cyber.MA || 0)
    const cat = this.constructor.categoryFor(body)

    const run = ma * 3
    this.set("save", body)
    this.set("btm", this.constructor.BTM[cat])
    this.set("bdm", this.signed(this.constructor.BDM[cat]))
    this.set("run", run)
    this.set("leap", Math.floor(run / 4))
    this.set("carry", body * 10)
    this.set("initiative", ref + (await this.saInitiativeBonus()))
    this.setHumanityLoss()
    await this.setAwareness()
  }

  // Always-on cyberware stat bonuses { REF, BODY, MA, ... }, asked of the cyberware
  // editor via outlet. Empty until the editor connects (recompute re-fires on its
  // outletConnected + on the equipment-cost change message).
  async cyberStatBonuses() {
    if (!this.hasCp2020CyberwareOutlet) return {}
    return this.cp2020CyberwareOutlet.statBonuses()
  }

  // HL — total Humanity Cost of installed cyberware, pulled from the cyberware
  // editor. Sum may be fractional (HC seeds from dice-averages); trim float noise.
  setHumanityLoss() {
    if (!this.hasHlTarget) return
    if (!this.hasCp2020CyberwareOutlet) return // editor not connected yet

    const hl = this.cp2020CyberwareOutlet.humanityLoss()
    this.hlTarget.textContent = String(Math.round(hl * 10) / 10)
  }

  // Awareness/Notice "base / combat": base is the skill's effective value (asked
  // of the skills editor — handles untrained -2); combat adds the SA combat-only
  // awareness bonus (e.g. Combat Sense), which applies ONLY in combat. Always show
  // both, even when equal (12 / 12).
  async setAwareness() {
    if (!this.hasAwarenessTarget) return
    if (!this.hasCp2020SkillsOutlet) return // editor not connected yet

    const base = this.cp2020SkillsOutlet.effectiveValue("awareness_notice")
    const combat = base + (await this.saAwarenessBonus())
    this.awarenessTarget.textContent = `${base} / ${combat}`
  }

  // SA combat-awareness bonus: sum floor(level/divisor) for any active SA with an
  // awareness_notice skill target in sa-skill-bonuses.json (combat-only here).
  async saAwarenessBonus() {
    return this.saBonusForTarget((t) => t?.kind === "skill" && t?.slug === "awareness_notice")
  }

  // Current value of a base stat = base + bonus, read live from the form.
  stat(name) {
    return this.field(`stat_${name}`) + this.field(`stat_${name}_bonus`)
  }

  field(name) {
    return this.hasCp2020SheetOutlet ? this.cp2020SheetOutlet.intField(name) : 0
  }

  // Sum floor(level / divisor) across the character's SAs, for every SA bonus rule
  // whose target matches `predicate`. Shared by initiative and combat awareness; both
  // fold an SA's level into a derived value. The SAs (slug + level) are asked of the
  // skills editor — SAs are skill rows now, not level_sa_N columns.
  async saBonusForTarget(predicate) {
    const bonuses = await DataLoader.systemGet("sa-skill-bonuses")
    let total = 0
    for (const { slug, level } of this.specialAbilities()) {
      for (const rule of bonuses?.[slug] ?? []) {
        if (predicate(rule.target)) total += Math.floor(level / (rule.divisor || 1))
      }
    }
    return total
  }

  // The character's Special Abilities [{ slug, level }], pulled from the skills editor
  // via outlet (empty until it connects; recompute re-fires on skills-changed).
  specialAbilities() {
    return this.hasCp2020SkillsOutlet ? this.cp2020SkillsOutlet.specialAbilities() : []
  }

  saInitiativeBonus() {
    return this.saBonusForTarget((t) => t?.kind === "other" && t?.name === "initiative")
  }

  // Write a computed value into its target span, if present.
  set(name, value) {
    const has = `has${name[0].toUpperCase()}${name.slice(1)}Target`
    if (this[has]) this[`${name}Target`].textContent = String(value)
  }

  signed(n) {
    return n > 0 ? `+${n}` : String(n)
  }
}
